import type { AssetOverview } from '@fleetspoke/core/types';
import { palette, stateChips, type AssetDisplayState } from '@fleetspoke/ui/tokens';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

/**
 * "On transfer T-0003" beside an asset's state: an asset on an open transfer is not free stock (it is left out of
 * New transfer and requests) even while it still reads "on site" at the origin. Opens the transfer.
 */
export function OpenTransferChip({
  a,
}: {
  a: Pick<AssetOverview, 'open_transfer_id' | 'open_transfer_ref'>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!a.open_transfer_id) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      color="info"
      icon={<LocalShippingOutlinedIcon />}
      label={
        a.open_transfer_ref
          ? t('board.on_transfer', { ref: a.open_transfer_ref })
          : t('transfers.ref')
      }
      onClick={(e) => {
        e.stopPropagation();
        void navigate(`/transfers/${a.open_transfer_id}`);
      }}
      data-testid="on-transfer"
    />
  );
}

/** Asset state chip: always text + colour, never colour alone (UI design §2). */
export function StateChip({ state, detail }: { state: AssetDisplayState; detail?: string | null }) {
  const { t } = useTranslation();
  const mode = useTheme().palette.mode;
  const chip = stateChips[state] ?? stateChips.unknown;
  const label = detail ? `${t(chip.labelKey)} · ${detail}` : t(chip.labelKey);
  return (
    <Chip
      size="small"
      label={label}
      data-state={state}
      sx={{
        color: palette[chip.fg][mode],
        bgcolor: palette[chip.bg][mode],
        border: `1px solid ${palette[chip.fg][mode]}`,
        fontWeight: 500,
        maxWidth: '100%',
      }}
    />
  );
}
